import { flow } from 'fp-ts/function';
import { at, getOr, isEmpty, map } from 'lodash/fp';
import pt from 'prop-types';
import { Component, Fragment } from 'react';
import { Fields } from 'redux-form';
import { setEarliestAcceptableEndDate } from '@mews-commander/form';
import { LegacyCommanderContextValue, ResponsiveTwoColumns } from '@mews-commander/ui';
import { updateNilEventHandlersWithNoop } from '../../utils';
import { validation } from '../../validation';
import DateStringPickerField from './DateStringPickerField';

const DEFAULT_AUTOFILL_DAY_DIFFERENCE = 1;

const dateIntervalFieldsFactory = ({ DateField = DateStringPickerField } = {}) =>
    class DateIntervalFields extends Component {
        declare context: LegacyCommanderContextValue;
        static contextTypes = {
            getCalendarService: pt.func.isRequired,
        };

        static propTypes = {
            autofillDayDifference: pt.number,
            autoCorrect: pt.bool,
            constraints: pt.oneOfType([pt.shape({}), pt.func]).isRequired,
            fieldNames: pt.arrayOf(pt.string).isRequired,
            minDayDifference: pt.number,
            maxDayDifference: pt.number,
            correctionPolicy: pt.func,
            startProps: pt.shape({}),
            endProps: pt.shape({}),
            inline: pt.bool,
        };

        static defaultProps = {
            autofillDayDifference: DEFAULT_AUTOFILL_DAY_DIFFERENCE,
            minDayDifference: 0,
            autoCorrect: true,
            correctionPolicy: setEarliestAcceptableEndDate,
            inline: false,
        };

        autoCorrectDateRange = ({ startDateInput, endDateInput, startDateMeta, endDateMeta }: $TSFixMe) => {
            const { getCalendarService } = this.context;
            // @ts-expect-error $TSFixMeError: Property 'correctionPolicy' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
            const { correctionPolicy, minDayDifference, maxDayDifference } = this.props;
            const {
                getDateFromDateString,
                getDateString,
                isDateGreater,
                isValidDateString,
                addDays,
                isDateLessOrEqual,
                isDateGreaterOrEqual,
            } = getCalendarService();

            const dirty = startDateMeta.dirty || endDateMeta.dirty;

            if (dirty && isValidDateString(startDateInput.value) && isValidDateString(endDateInput.value)) {
                const startDate = getDateFromDateString(startDateInput.value);
                const endDate = getDateFromDateString(endDateInput.value);

                const isWithinMinDayDifference = minDayDifference
                    ? isDateLessOrEqual(addDays(startDate, minDayDifference), endDate)
                    : true;
                const isWithinMaxDayDifference = maxDayDifference
                    ? isDateGreaterOrEqual(addDays(startDate, maxDayDifference), endDate)
                    : true;

                if (isDateGreater(startDate, endDate) || !isWithinMinDayDifference || !isWithinMaxDayDifference) {
                    const { newStartDate = null, newEndDate = null } = correctionPolicy({
                        startDate,
                        endDate,
                        getCalendarService,
                        ...this.props,
                    });
                    if (newStartDate !== startDate) {
                        startDateInput.onChange(newStartDate && getDateString(newStartDate));
                    }
                    if (newEndDate !== endDate) {
                        endDateInput.onChange(newEndDate && getDateString(newEndDate));
                    }
                }
            }
        };

        renderDateFields = ({ names, ...fields }: $TSFixMe) => {
            const { getCalendarService } = this.context;
            // @ts-expect-error $TSFixMeError: Property 'startProps' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
            const { startProps, endProps, minDayDifference, maxDayDifference, minDate, maxDate, autoCorrect, inline } =
                this.props;

            const { addDays, getDateFromDateString, isValidDateString, getMaxDate } = getCalendarService();
            const [startFieldProps, endFieldProps] = map(updateNilEventHandlersWithNoop, at(names, fields));

            const startDate =
                isValidDateString(startFieldProps.input.value) && getDateFromDateString(startFieldProps.input.value);
            const endDate =
                isValidDateString(endFieldProps.input.value) && getDateFromDateString(endFieldProps.input.value);
            const endDateBase = minDate && startDate ? getMaxDate([minDate, startDate]) : minDate || startDate;
            const minEndDate = endDateBase && minDayDifference ? addDays(endDateBase, minDayDifference) : endDateBase;

            if (autoCorrect) {
                this.autoCorrectDateRange({
                    startDateInput: startFieldProps.input,
                    endDateInput: endFieldProps.input,
                    startDateMeta: startFieldProps.meta,
                    endDateMeta: endFieldProps.meta,
                });
            }

            const startDateField = (
                <DateField
                    name={startFieldProps.input.name}
                    minDate={minDate}
                    maxDate={maxDate}
                    {...startFieldProps}
                    {...startProps}
                />
            );
            const endDateField = (
                <DateField
                    minDate={minEndDate}
                    maxDate={(startDate && maxDayDifference && addDays(startDate, maxDayDifference)) || maxDate}
                    name={endFieldProps.input.name}
                    date={endDate || minEndDate}
                    {...endFieldProps}
                    {...endProps}
                />
            );

            return inline ? (
                <ResponsiveTwoColumns>
                    {startDateField}
                    {endDateField}
                </ResponsiveTwoColumns>
            ) : (
                <Fragment>
                    {startDateField}
                    {endDateField}
                </Fragment>
            );
        };

        validate = (value: $TSFixMe, allValues: $TSFixMe, props: $TSFixMe, name: $TSFixMe) => {
            if (!isEmpty(allValues)) {
                const validationMessage = flow(validation((this.props as $TSFixMe).constraints), getOr(null, name))(
                    allValues,
                    props
                );
                return validationMessage;
            }
        };

        createValidation = () => {
            const [startFieldName, endFieldName] = (this.props as $TSFixMe).fieldNames;
            return {
                [startFieldName]: this.validate,
                [endFieldName]: this.validate,
            };
        };

        render() {
            return (
                <Fields
                    component={this.renderDateFields}
                    names={(this.props as $TSFixMe).fieldNames}
                    validate={this.createValidation()}
                    parse={(this.props as $TSFixMe).parse}
                />
            );
        }
    };

export default dateIntervalFieldsFactory();
